import groupBy from 'lodash/groupBy';
import { toast } from 'chameleon/ui-stack/ui/toast';

/**
 * Helper to decide how many classes and students an assessment was assigned to.
 * Keep track of full classes and ISAs.
 * @param {array} formAssignees - Assignees selected in the form (some of them might be ISA)
 * @param {array} formStudents - List of students in the form (some of them might be part of a full class)
 * @param {array} availableSections - All available assignees in the assign form.
 * */
export const getAssignedISAInfo = (
  formAssignees,
  formStudents,
  availableSections,
) => {
  let isSingleISA;
  let isMultipleAssignWithISA;
  let totalAssignees = [...formAssignees];
  let className = null;

  // Get ISA from form students.
  const studentsByAssignee = groupBy(formStudents, 'sectionId');
  const formISAs = availableSections.filter(
    (section) =>
      studentsByAssignee[section.id] &&
      studentsByAssignee[section.id].length < section.students.length,
  );

  // Check if there are any ISAs involved in the process.
  if (formISAs.length > 0) {
    const formIsaIds = formISAs.map((isa) => isa.id);
    const otherFormAssignees = formAssignees.filter(
      (assignee) => !formIsaIds.includes(assignee.sectionId),
    );
    isSingleISA = formISAs.length === 1 && otherFormAssignees.length === 0;
    isMultipleAssignWithISA =
      formISAs.length > 1 ||
      (formISAs.length === 1 && otherFormAssignees.length > 0);

    if (isSingleISA) {
      className = formISAs[0].name;
    }
  } else {
    isSingleISA = false;
    isMultipleAssignWithISA = false;

    if (formAssignees.length === 1) {
      className = formAssignees[0].name;
    }
  }

  // Compute total assignees. Is used to be send through callback.
  const formAssigneesIds = formAssignees.map((assignee) => {
    // some place we pass the actual section array and other place we pass the students array to get the sections
    const sectionId = assignee.sectionId || assignee.id;
    return sectionId.toString();
  });

  const assigneeIdsFromStudents = Object.keys(studentsByAssignee).filter(
    (assignee) => !formAssigneesIds.includes(assignee),
  );
  const formEveryOneElse = formISAs.filter((formisa) =>
    formAssigneesIds.includes(formisa.id),
  );
  const formEveryOneElseIds = formEveryOneElse.map((elem) => elem.id);
  if (formEveryOneElseIds.length > 0) {
    totalAssignees = totalAssignees.filter(
      (elem) => !formEveryOneElseIds.includes(elem.id),
    );
  }

  const assigneesFromStudents = availableSections
    .filter(
      (assignee) =>
        assigneeIdsFromStudents.includes(assignee.id) ||
        formEveryOneElseIds.includes(assignee.id),
    )
    .map((assignee) => ({
      ...assignee,
      subStudents: studentsByAssignee[assignee.id],
      isIndividualAssignment:
        studentsByAssignee[assignee.id] <
        availableSections.find((s) => assignee.id === s.id).students,
    }));

  totalAssignees = totalAssignees.concat(assigneesFromStudents);

  return {
    isSingleISA,
    isMultipleAssignWithISA,
    className,
    totalAssignees,
  };
};

/**
 * Get the notification message for assign action.
 * @returns {string} - Assign notification message
 * */
export const getNotificationMessage = (
  isSingleISA,
  isMultipleAssignWithISA,
  className,
  totalAssignees,
  formStudents,
) => {
  const totalStudentsLength = formStudents.length;

  if (isSingleISA) {
    let studentsText;

    if (totalStudentsLength === 1) {
      const [{ firstName, lastName }] = formStudents;
      studentsText = `${firstName} ${lastName}`;
    } else {
      studentsText = `${totalStudentsLength} students`;
    }

    return `Assigned to ${studentsText} in ${className}`;
  }

  if (isMultipleAssignWithISA) {
    return `Assigned to ${formStudents.length} students`;
  }

  const assigneesLength = totalAssignees.length;
  if (assigneesLength === 1) {
    return `Assigned to ${className}`;
  }

  return `Assigned to ${assigneesLength} classes`;
};

/**
 * Show Toast notification based on assign types
 * */
export const showToastNotification = (
  assignmentsResponse,
  isSingleISA,
  isMultipleAssignWithISA,
  className,
  totalAssignees,
  formStudents,
  subjectId,
) => {
  let notificationMessage = '';
  const totalStudentsLength = formStudents.length;
  const assessmentTitle = assignmentsResponse[0].title;

  const getAssignmentLink = (assignmentId) =>
    `/${subjectId}/assignments/progress/${assignmentId}`;

  let sectionsWithAssignmentIds = {};
  const actionText = 'Manage Assignment';
  let assignmentLink = '';

  assignmentsResponse.forEach((ar) => {
    const { assignment_ids: assignmentIds } = ar;
    sectionsWithAssignmentIds = {
      ...sectionsWithAssignmentIds,
      ...assignmentIds,
    };
  });

  if (isSingleISA) {
    let studentsText;

    if (totalStudentsLength === 1) {
      const [{ firstName, lastName }] = formStudents;
      studentsText = `${firstName} ${lastName}`;
    } else {
      studentsText = `${totalStudentsLength} students`;
    }
    notificationMessage = `${assessmentTitle} assigned to ${studentsText} in ${className}`;
    assignmentLink = getAssignmentLink(assignmentsResponse[0].id);
    toast(notificationMessage, { actionLink: assignmentLink, actionText });
    return;
  }

  if (isMultipleAssignWithISA) {
    const studentsBySections = groupBy(formStudents, 'sectionId');
    // avoid duplicate toast to show due to Re-assigning
    const shownToast = {};
    for (let i = 0; i < totalAssignees.length; i++) {
      // if the assign is an ISA
      let {
        students = [],
        name: sectionName,
        id: sectionId,
      } = totalAssignees[i];
      // some sectionId have __ in them
      assignmentLink = getAssignmentLink(sectionsWithAssignmentIds[sectionId]);
      if (sectionId.includes('__')) {
        const [originalSectionId] = sectionId.split('__');
        assignmentLink = getAssignmentLink(
          sectionsWithAssignmentIds[originalSectionId],
        );
      }
      const isISA = students.length > 0;
      if (isISA) {
        let studentsText = '';
        const currentAssignedStudents = studentsBySections[sectionId];
        if (currentAssignedStudents.length === 1) {
          const [{ firstName, lastName }] = currentAssignedStudents;
          studentsText = `${firstName} ${lastName}`;
        } else {
          studentsText = `${currentAssignedStudents.length} students`;
        }
        notificationMessage = `${assessmentTitle} assigned to ${studentsText} in ${sectionName}`;
      } else {
        // otherwise it's a full class
        notificationMessage = `${assessmentTitle} assigned to ${sectionName}`;
      }
      if (!shownToast.hasOwnProperty(sectionName)) {
        toast(notificationMessage, { actionLink: assignmentLink, actionText });
        shownToast[sectionName] = sectionName;
      }
    }
    return;
  }

  const assigneesLength = totalAssignees.length;
  if (assigneesLength === 1) {
    notificationMessage = `${assessmentTitle} assigned to ${className}`;
    assignmentLink = getAssignmentLink(assignmentsResponse[0].id);
    toast(notificationMessage, { actionLink: assignmentLink, actionText });
    return;
  }

  if (assigneesLength > 1) {
    // avoid duplicate toast to show due to Re-assigning
    const shownToast = {};
    // show multiple toast for assigneesLength
    for (let i = 0; i < assigneesLength; i++) {
      let { name: sectionName, id: sectionId } = totalAssignees[i];
      // some sectionId have __ in them
      if (sectionId.includes('__')) {
        [sectionId] = sectionId.split('__');
      }
      assignmentLink = getAssignmentLink(sectionsWithAssignmentIds[sectionId]);
      notificationMessage = `${assessmentTitle} assigned to ${sectionName}`;
      if (!shownToast.hasOwnProperty(sectionName)) {
        toast(notificationMessage, { actionLink: assignmentLink, actionText });
        shownToast[sectionName] = sectionName;
      }
    }
  }
};
